import { CheckIcon } from "@heroicons/react/16/solid";
import { clsx } from "clsx";

export interface ChoiceChipsOption {
    id: string;
    label: string;
    /** Shown, but cannot be flipped: the last one that has to stay on. */
    disabled?: boolean;
}

/**
 * A handful of choices, any number of which can be on, all in view. For a set
 * small enough to read at a glance; a longer one belongs in
 * `ComboBoxMultiple`, which hides its options until asked.
 */
export function ChoiceChips({
    label,
    options,
    value,
    onChange,
    disabled = false,
}: {
    /** What the group chooses, for a screen reader. */
    label: string;
    options: ChoiceChipsOption[];
    /** The ids that are on. */
    value: string[];
    onChange: (value: string[]) => void;
    disabled?: boolean;
}) {
    return (
        <div
            role={"group"}
            aria-label={label}
            className={clsx("flex flex-wrap gap-2", disabled && "opacity-50")}
        >
            {options.map((option) => {
                const on = value.includes(option.id);
                return (
                    <button
                        key={option.id}
                        type={"button"}
                        aria-pressed={on}
                        disabled={disabled || option.disabled}
                        onClick={() =>
                            onChange(
                                on
                                    ? value.filter((id) => id !== option.id)
                                    : [...value, option.id],
                            )
                        }
                        className={clsx(
                            // As tall as a field, so a row of chips lines up
                            // with its label the way an input does.
                            "inline-flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-sm ring-1 ring-inset transition-colors",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500",
                            on
                                ? "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-400/10 dark:text-indigo-300 dark:ring-indigo-400/20"
                                : "text-zinc-700 ring-zinc-950/10 hover:ring-zinc-950/20 dark:text-zinc-300 dark:ring-white/10 dark:hover:ring-white/20",
                            (disabled || option.disabled) && "cursor-default",
                        )}
                    >
                        {on && (
                            <CheckIcon
                                className={"size-4"}
                                aria-hidden={"true"}
                            />
                        )}
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
